// Smart title + units helpers for Mobilizations.
//
// Title rules:
//   `{SCOPE_CODE}[-B{building}][-F{floor}][-A{area}]`
//   - Append `-B…` only when ALL items share the exact same non-empty building.
//   - Append `-F…` only when ALL items share the exact same non-empty floor.
//   - Append `-A…` only when ALL items share the exact same non-empty area.
//   - If no hierarchy applies (rooms only), fall back to `{SCOPE_CODE}-{N}rms`.
//   - If even the scope is missing, fall back to "Mob".
//
// Units rules:
//   `{N}rms {N}u`. Either side is omitted when its count is 0.
//
// Both helpers normalize the building/floor/area strings (trim + strip the
// "Building " prefix, drop a trailing ".0") so values like "Building A" and
// "A" collapse into "A".

function _norm(v) {
  if (v === null || v === undefined) return "";
  let s = String(v).trim();
  if (!s) return "";
  // Strip leading "Building "/"Bldg " noise so headers stay short.
  s = s.replace(/^Building\s+/i, "").replace(/^Bldg\s+/i, "");
  // Excel floats like "2.0" → "2".
  if (/^-?\d+\.0+$/.test(s)) s = s.replace(/\.0+$/, "");
  return s;
}

function _uniformValue(values) {
  const set = new Set();
  for (const v of values) {
    const n = _norm(v);
    if (n) set.add(n);
  }
  if (set.size === 1) return [...set][0];
  return "";
}

function _scopeCode(rawScope, scopeMap = {}) {
  const full = String(rawScope || "").split(",")[0].trim();
  if (!full) return "";
  const lower = full.toLowerCase();
  if (scopeMap[lower]) return scopeMap[lower];
  const words = full.split(/\s+/).filter(Boolean);
  // Single-word scopes stay whole (BIKE, FPS, POST, TC…). Multi-word scopes
  // become first-letter acronyms (Toilet Room Accessories → TRA).
  if (words.length <= 1) return (words[0] || full).slice(0, 6).toUpperCase();
  return words.map((w) => w[0] || "").join("").slice(0, 4).toUpperCase();
}

function _composeTitle({ scope, building, floor, area }) {
  // Compact hierarchy: scope · B{bldg} · F{floor} · A{area}
  // Each piece is included only when present. Room count is intentionally
  // NOT included — it's shown on the dedicated `units` line ("2rms 13u").
  const parts = [];
  if (scope) parts.push(scope);
  if (building) parts.push(`B${building}`);
  if (floor) parts.push(`F${floor}`);
  if (area) parts.push(`A${area}`);
  if (parts.length === 0) return "Mob";
  return parts.join("-");
}

// Verbose, human-readable location tag for cards (no rooms suffix —
// rooms/units are shown on the dedicated `units` line). Examples:
//   building=B, floor=1            → "Bldg B Flr 1"
//   building=A, floor=4, area=C   → "Bldg A Flr 4 Area C"
//   floor=2 only                   → "Flr 2"
//   nothing                         → ""
function _composeVerboseLocation({ building, floor, area }) {
  const parts = [];
  if (building) parts.push(`Bldg ${building}`);
  if (floor) parts.push(`Flr ${floor}`);
  if (area) parts.push(`Area ${area}`);
  return parts.join(" ");
}

function _composeUnits({ totalRooms, totalUnits }) {
  const r = Number(totalRooms) || 0;
  const u = Number(totalUnits) || 0;
  const bits = [];
  if (r > 0) bits.push(`${r}rms`);
  if (u > 0) bits.push(`${u}u`);
  return bits.join(" ");
}

// Given selected FRS items, build {title, units}.
//   items: [{ scope_type, scope_code, building, floor, area, room_number, quantity }]
//   scopeMap: { [trade.toLowerCase()]: code }  (optional fallback when item.scope_code is empty)
export function smartTitleFromItems(items, scopeMap = {}) {
  const list = items || [];
  if (!list.length) return { title: "Mob", units: "" };

  // Prefer scope_code on the row itself; else resolve from scope_type.
  const codes = list.map((i) => (i.scope_code || _scopeCode(i.scope_type, scopeMap)));
  const scope = _uniformValue(codes) || codes[0] || "";

  const building = _uniformValue(list.map((i) => i.building));
  const floor = _uniformValue(list.map((i) => i.floor));
  const area = _uniformValue(list.map((i) => i.area));

  const roomKeys = new Set();
  let totalUnits = 0;
  for (const i of list) {
    const key = `${_norm(i.building)}|${_norm(i.floor)}|${_norm(i.room_number)}`;
    if (_norm(i.room_number)) roomKeys.add(key);
    totalUnits += Number(i.quantity || 0);
  }
  const totalRooms = roomKeys.size;

  return {
    title: _composeTitle({ scope, building, floor, area, totalRooms }),
    units: _composeUnits({ totalRooms, totalUnits }),
    verboseLocation: _composeVerboseLocation({ building, floor, area }),
    scope,
    building,
    floor,
    area,
    totalRooms,
    totalUnits,
  };
}

// Given a saved Mobilization document, build {title, units}.
// Backend stores building/floor/area only when uniform across items.
export function smartTitleFromMob(mob, scopeMap = {}) {
  if (!mob) return { title: "Mob", units: "", verboseLocation: "" };
  const scope = _norm(mob.scope_code) || _scopeCode(mob.scope_type, scopeMap);
  const building = _norm(mob.building);
  const floor = _norm(mob.floor);
  const area = _norm(mob.area);
  const totalRooms = Number(mob.total_rooms) || 0;
  const totalUnits = Number(mob.total_quantity) || 0;
  // A foreman-typed title wins over the generated one.
  const composed = mob.title_custom && (mob.title || "").trim()
    ? mob.title.trim()
    : _composeTitle({ scope, building, floor, area, totalRooms });
  return {
    title: composed,
    units: _composeUnits({ totalRooms, totalUnits }),
    verboseLocation: _composeVerboseLocation({ building, floor, area }),
    scope,
    building,
    floor,
    area,
    totalRooms,
    totalUnits,
  };
}
